import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, Tag, Avatar, Icon, FlexBox, Button, BusyIndicator } from '@ui5/webcomponents-react';
import { controlDevice, fetchDeviceStatus } from './api';
import RokuRemote from './RokuRemote';
import { resolveRokuIp, cachedRokuIp } from './rokuDiscovery';
import { queryActiveApp } from './rokuEcp';

function timeSince(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60)        return secs + 's ago';
  if (secs < 3600)      return Math.floor(secs / 60) + 'm ago';
  if (secs < 86400)     return Math.floor(secs / 3600) + 'h ago';
  return Math.floor(secs / 86400) + 'd ago';
}

function DeviceCard({ device, locationUUID, guestCode }) {
  const [imgError, setImgError]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [lastCmd, setLastCmd]     = useState(null);  // 'on' | 'off' | null
  const [error, setError]         = useState(false);
  const [bgError, setBgError]     = useState(false);
  const [lockState, setLockState] = useState(null);   // 'Locked' | 'Unlocked' | null
  const [poolData,  setPoolData]  = useState(null);   // iAqualink home_screen data
  const [haikuData, setHaikuData] = useState(null);   // Haiku fan state (speed, temp)
  const [statusLoading, setStatusLoading] = useState(false);

  const [rokuOpen, setRokuOpen] = useState(false);
  const [rokuActiveApp, setRokuActiveApp] = useState('');
  const [sonosData, setSonosData]     = useState(null); // { playbackState, trackName, artistName, imageUrl }
  const [sonosImgError, setSonosImgError] = useState(false);
  const [statusError, setStatusError] = useState(false);

  const isControl  = device.permission === 'CONTROL';
  const isShelly   = device.account_type === 'Shelly';
  // Shelly is now controllable from the web via the WebSocket bridge in
  // qrController::guestDeviceControl, so ignore the legacy appOnly flag for
  // Shelly. (externalTypes.appOnly was historically 1 because there was no
  // non-app path — the new guest flow makes that stale.)
  const isAppOnly  = parseInt(device.appOnly) === 1 && !isShelly;
  const canControl = isControl && !isAppOnly;
  const isLock     = /lock/i.test(device.account_type) || /schlage|yale|august|kwikset|nuki|igloohome|tedee|salto/i.test(device.account_type);
  const isPool     = device.account_type === 'iAqualink';
  const isSonos    = device.account_type === 'Sonos' || device.account_type === 'Sonos-All';
  const isHaiku    = device.account_type === 'Haiku';
  const isRoku = device.account_type === 'Roku-All' || device.account_type === 'Roku';
  const isNative = !!(window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform());
  // Roku control is LAN-only (ECP on :8060) — the native WebView reaches the
  // TV directly on the guest Wi-Fi, no cloud auth involved. Since there's no
  // risk of off-property abuse, allow VIEW-permission guests to open the
  // remote too (a VIEW-only Shelly still can't actuate — that hits cloud).
  const canOpenRoku = isRoku && isNative;
  const hasCameraImage = !!device.s3Url && !bgError;
  const capturedAgo = device.s3Date ? timeSince(device.s3Date) : '';

  // Fetch initial status for locks + pool devices.
  const refreshStatus = useCallback(async () => {
    if (!isLock && !isPool && !isSonos && !isHaiku) return;
    setStatusLoading(true);
    try {
      const result = await fetchDeviceStatus(locationUUID, device.device_id, device.account_type);
      if (result && result.error) {
        setStatusError(result.error);
      } else {
        setStatusError(false);
        if (result && result.lockState)  setLockState(result.lockState);
        if (result && result.poolData)   setPoolData(result.poolData);
        if (result && result.sonosData)  setSonosData(result.sonosData);
        if (result && result.haikuData)  setHaikuData(result.haikuData);
      }
    } catch (e) {
      setStatusError('Unable to load status');
    } finally {
      setStatusLoading(false);
    }
  }, [isLock, isPool, isSonos, isHaiku, locationUUID, device.device_id, device.account_type]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Roku "Now Playing" probe — LAN-only (native WebView). Discover the IP
  // (cached after first scan) and hit ECP /query/active-app. Re-poll every
  // 30s while mounted so the label tracks when the guest changes channels.
  useEffect(() => {
    if (!isRoku || !isNative) return undefined;
    const serial = device.device_id;
    if (!serial) return undefined;
    let cancelled = false;
    let timer = null;

    const fetchActive = async (ip) => {
      if (!ip || cancelled) return;
      try {
        const { activeApp } = await queryActiveApp(ip);
        if (!cancelled) setRokuActiveApp(activeApp || '');
      } catch (e) { /* network blip — leave prior label */ }
    };

    (async () => {
      let ip = cachedRokuIp(serial);
      if (!ip) {
        try { ip = await resolveRokuIp(serial); } catch (e) { ip = ''; }
      }
      if (cancelled || !ip) return;
      fetchActive(ip);
      timer = setInterval(() => fetchActive(ip), 30000);
    })();

    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [isRoku, isNative, device.device_id]);

  const logoSrc = `/img/logos/${device.account_type}.png`;

  const avatar = imgError ? (
    <Avatar icon="home" colorScheme="Accent6" size="S" />
  ) : (
    <img
      src={logoSrc}
      width="32"
      height="32"
      alt={device.account_type}
      style={{ objectFit: 'contain', borderRadius: '6px' }}
      onError={() => setImgError(true)}
    />
  );

  const permissionTag = (
    <FlexBox gap="0.375rem" alignItems="Center">
      {isControl
        ? <Tag design="Set2" colorScheme="3">Control</Tag>
        : <Tag design="Set2" colorScheme="6">View</Tag>
      }
      {isAppOnly && <Tag design="Set2" colorScheme="8">App Only</Tag>}
    </FlexBox>
  );

  async function handleCommand(cmd) {
    setLoading(true);
    setError(false);
    setLastCmd(null);
    try {
      const result = await controlDevice(locationUUID, device.device_id, device.account_type, cmd, guestCode);
      if (result.error) {
        setError(true);
      } else {
        setLastCmd(cmd);
        // Allegion can take a beat to reflect the new state — give it a moment
        // before re-reading. Fall back to optimistic state if server is slow.
        if (isLock) {
          setLockState(cmd === 'on' ? 'Unlocked' : 'Locked');
          setTimeout(() => { refreshStatus(); }, 1500);
        }
      }
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  async function handleSonosCommand(cmd) {
    setLoading(true);
    setError(false);
    try {
      const result = await controlDevice(locationUUID, device.device_id, device.account_type, cmd, guestCode);
      if (result.error) {
        setError(true);
      } else {
        // Optimistic state update while we wait for the real refresh
        if (cmd === 'play')  setSonosData(prev => prev ? { ...prev, playbackState: 'PLAYBACK_STATE_PLAYING' } : prev);
        if (cmd === 'pause') setSonosData(prev => prev ? { ...prev, playbackState: 'PLAYBACK_STATE_PAUSED'  } : prev);
        setTimeout(() => { refreshStatus(); }, 1200);
      }
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className={`grid-card device-card${hasCameraImage ? ' device-card-camera' : ''}`}>
      {hasCameraImage && (
        <div
          className="device-card-bg"
          style={{ backgroundImage: `url(${device.s3Url})` }}
        >
          <img src={device.s3Url} alt="" style={{ display: 'none' }} onError={() => setBgError(true)} />
          {capturedAgo && <span className="device-card-bg-time">{capturedAgo}</span>}
        </div>
      )}

      <CardHeader
        avatar={avatar}
        titleText={
          // Haiku fans are typically stored with a generic "Haiku" display
          // name; the real fan label lives in the vendor state (settings.name
          // → devices.name → group). Surface that once we've fetched status.
          (isHaiku && haikuData && haikuData.name)
            ? haikuData.name
            : (device.display_name || device.account_type)
        }
        subtitleText={
          (isHaiku && haikuData && haikuData.name && haikuData.name !== device.display_name)
            ? `Haiku · ${device.display_name || 'Fan'}`
            : (isRoku && rokuActiveApp)
              ? `Roku · ${rokuActiveApp}`
              : device.account_type
        }
        action={permissionTag}
        interactive={false}
      />

      {isPool && (
        <div className="device-card-status device-card-pool-status">
          {statusLoading ? (
            <BusyIndicator active size="Small" />
          ) : statusError ? (
            <span className="pool-stat-label" style={{ color: 'var(--sapNegativeColor, #b00)', lineHeight: 1.4 }}>
              {typeof statusError === 'string' ? statusError : 'Status unavailable'}
            </span>
          ) : poolData ? (
            <FlexBox direction="Column" gap="0.35rem" style={{ width: '100%' }}>
              {/* Temperature row */}
              <FlexBox gap="1rem" alignItems="Center" wrap="Wrap">
                {poolData.spa_temp && (
                  <FlexBox gap="0.3rem" alignItems="Center">
                    <Icon name="temperature" style={{ fontSize: '0.9rem', color: 'var(--sapContent_IconColor, #6a6d70)' }} />
                    <span className="pool-stat-label">Spa</span>
                    <span className="pool-stat-value">{poolData.spa_temp}°{poolData.temp_scale}</span>
                  </FlexBox>
                )}
                {poolData.pool_temp && (
                  <FlexBox gap="0.3rem" alignItems="Center">
                    <Icon name="temperature" style={{ fontSize: '0.9rem', color: 'var(--sapContent_IconColor, #6a6d70)' }} />
                    <span className="pool-stat-label">Pool</span>
                    <span className="pool-stat-value">{poolData.pool_temp}°{poolData.temp_scale}</span>
                  </FlexBox>
                )}
                {poolData.air_temp && (
                  <FlexBox gap="0.3rem" alignItems="Center">
                    <Icon name="weather-sunny" style={{ fontSize: '0.9rem', color: 'var(--sapContent_IconColor, #6a6d70)' }} />
                    <span className="pool-stat-label">Air</span>
                    <span className="pool-stat-value">{poolData.air_temp}°{poolData.temp_scale}</span>
                  </FlexBox>
                )}
                {!poolData.spa_temp && !poolData.pool_temp && !poolData.air_temp && (
                  <span className="pool-stat-label">No temperature data</span>
                )}
              </FlexBox>
              {/* Heating status row */}
              {(poolData.spa_heater === 'on' || poolData.pool_heater === 'on') && (
                <FlexBox gap="0.4rem" alignItems="Center">
                  <Icon name="heating-cooling" style={{ fontSize: '0.9rem', color: 'var(--sapCriticalColor, #df6e0c)' }} />
                  <span className="pool-heating-label">
                    Heating
                    {poolData.spa_heater === 'on' && poolData.pool_heater === 'on' ? ' (Spa + Pool)'
                      : poolData.spa_heater === 'on' ? ' (Spa)'
                      : ' (Pool)'}
                  </span>
                </FlexBox>
              )}
            </FlexBox>
          ) : (
            <span className="pool-stat-label">Loading pool status…</span>
          )}
        </div>
      )}

      {isHaiku && (haikuData || statusLoading || statusError) && (
        <div className="device-card-status device-card-pool-status">
          {statusLoading && !haikuData ? (
            <BusyIndicator active size="Small" />
          ) : statusError && !haikuData ? (
            <span className="pool-stat-label" style={{ color: 'var(--sapNegativeColor, #b00)', lineHeight: 1.4 }}>
              {typeof statusError === 'string' ? statusError : 'Status unavailable'}
            </span>
          ) : haikuData ? (
            <FlexBox direction="Column" gap="0.35rem" style={{ width: '100%' }}>
              <FlexBox gap="1rem" alignItems="Center" wrap="Wrap">
                <FlexBox gap="0.3rem" alignItems="Center">
                  <Icon
                    name="wind"
                    style={{
                      fontSize: '0.9rem',
                      color: haikuData.is_on
                        ? 'var(--sapPositiveColor, #107e3e)'
                        : 'var(--sapContent_IconColor, #6a6d70)'
                    }}
                  />
                  <span className="pool-stat-label">Fan</span>
                  <span className="pool-stat-value">
                    {haikuData.is_on ? `Speed ${haikuData.fan_level} of 7` : 'Off'}
                  </span>
                </FlexBox>
                {haikuData.env_temp_c != null && haikuData.env_temp_c !== '' && (
                  <FlexBox gap="0.3rem" alignItems="Center">
                    <Icon name="temperature" style={{ fontSize: '0.9rem', color: 'var(--sapContent_IconColor, #6a6d70)' }} />
                    <span className="pool-stat-label">Room</span>
                    <span className="pool-stat-value">
                      {Math.round((parseFloat(haikuData.env_temp_c) * 9) / 5 + 32)}°F
                    </span>
                  </FlexBox>
                )}
              </FlexBox>
              {(haikuData.whoosh_enable || haikuData.eco_enable || haikuData.reverse_enable) && (
                <FlexBox gap="0.4rem" alignItems="Center" wrap="Wrap">
                  {haikuData.whoosh_enable && <Tag design="Set2" colorScheme="7">Whoosh</Tag>}
                  {haikuData.eco_enable    && <Tag design="Set2" colorScheme="8">Eco</Tag>}
                  {haikuData.reverse_enable && <Tag design="Set2" colorScheme="9">Reverse</Tag>}
                </FlexBox>
              )}
            </FlexBox>
          ) : (
            <span className="pool-stat-label">Loading fan status…</span>
          )}
        </div>
      )}

      {isLock && (lockState || statusLoading) && (
        <div className="device-card-status">
          {statusLoading && !lockState ? (
            <BusyIndicator active size="Small" />
          ) : (
            <FlexBox gap="0.375rem" alignItems="Center">
              <Icon
                name={lockState === 'Unlocked' ? 'unlocked' : 'locked'}
                style={{
                  fontSize: '1rem',
                  color: lockState === 'Unlocked'
                    ? 'var(--sapCriticalColor, #df6e0c)'
                    : 'var(--sapPositiveColor, #107e3e)'
                }}
              />
              <span className="device-card-status-label">{lockState || '—'}</span>
              {statusLoading && <BusyIndicator active size="Small" />}
            </FlexBox>
          )}
        </div>
      )}

      {isSonos && (canControl || sonosData || statusLoading || statusError) && (
        <div className="device-card-sonos">
          <FlexBox gap="0.75rem" alignItems="Center" style={{ width: '100%' }}>
            {/* Album art — row-level thumbnail, hidden on load error */}
            {sonosData?.imageUrl && !sonosImgError && (
              <img
                src={sonosData.imageUrl}
                alt="Album art"
                className="sonos-album-art"
                onError={() => setSonosImgError(true)}
              />
            )}
            {/* Track info — spinner while loading, text once loaded */}
            {statusLoading && !sonosData ? (
              <BusyIndicator active size="Small" style={{ flex: 1 }} />
            ) : statusError && !sonosData ? (
              <span className="sonos-artist-name" style={{ color: 'var(--sapNegativeColor, #b00)', flex: 1 }}>
                {typeof statusError === 'string' ? statusError : 'Status unavailable'}
              </span>
            ) : (
              <FlexBox direction="Column" gap="0.15rem" style={{ flex: 1, minWidth: 0 }}>
                <span className="sonos-track-name">
                  {sonosData ? (sonosData.trackName || 'Nothing Playing') : '—'}
                </span>
                {sonosData?.artistName && (
                  <span className="sonos-artist-name">{sonosData.artistName}</span>
                )}
              </FlexBox>
            )}
            {/* Transport controls — always visible for CONTROL devices */}
            {canControl && (
              <FlexBox gap="0.25rem" alignItems="Center" style={{ flexShrink: 0 }}>
                {loading ? (
                  <BusyIndicator active size="Small" />
                ) : (
                  <>
                    <Button design="Transparent" icon="media-rewind"  onClick={() => handleSonosCommand('prev')} />
                    <Button
                      design="Transparent"
                      icon={sonosData?.playbackState === 'PLAYBACK_STATE_PLAYING' ? 'media-pause' : 'media-play'}
                      onClick={() => handleSonosCommand(
                        sonosData?.playbackState === 'PLAYBACK_STATE_PLAYING' ? 'pause' : 'play'
                      )}
                    />
                    <Button design="Transparent" icon="media-forward" onClick={() => handleSonosCommand('next')} />
                  </>
                )}
              </FlexBox>
            )}
          </FlexBox>
        </div>
      )}

      {canOpenRoku && (
        <div className="device-card-controls">
          <Button design="Emphasized" icon="tv" onClick={() => setRokuOpen(true)}>
            Open Remote
          </Button>
        </div>
      )}

      <RokuRemote open={rokuOpen} onClose={() => setRokuOpen(false)} device={device} />

      {canControl && !isRoku && !isSonos && !isHaiku && (
        <div className="device-card-controls">
          {loading ? (
            <BusyIndicator active size="Small" />
          ) : (
            <FlexBox gap="0.5rem" alignItems="Center">
              <Button
                design="Positive"
                icon={isLock ? 'unlocked' : 'accept'}
                className={lastCmd === 'on' ? 'device-btn-active' : ''}
                onClick={() => handleCommand('on')}
              >
                {isLock ? 'Unlock' : 'On'}
              </Button>
              <Button
                design="Negative"
                icon={isLock ? 'locked' : 'decline'}
                className={lastCmd === 'off' ? 'device-btn-active' : ''}
                onClick={() => handleCommand('off')}
              >
                {isLock ? 'Lock' : 'Off'}
              </Button>
              {lastCmd && !error && (
                <Icon name="accept" style={{ color: 'var(--sapPositiveColor, #107e3e)', fontSize: '1rem' }} />
              )}
              {error && (
                <Icon name="error" style={{ color: 'var(--sapNegativeColor, #b00)', fontSize: '1rem' }} />
              )}
            </FlexBox>
          )}
        </div>
      )}
    </Card>
  );
}

export default DeviceCard;
